import { useState } from 'react';
import { Check, Copy, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useQuery } from '@tanstack/react-query';
import { teamService } from '@/services/teamService';

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateKey: (
    name: string, 
    scopes: string[], 
    environment: string, 
    expiresAt: string | null,
    role: string,
    ttlDays: number
  ) => void;
  newlyCreatedKey?: string | null;
}

const scopes = [
  { id: 'user', label: 'User' },
  { id: 'project', label: 'Project' },
  { id: 'team', label: 'Team' },
];

const environments = [
  { value: 'development', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
];

const roles = [
  { value: 'viewer', label: 'Viewer - Only /recall access' },
  { value: 'contributor', label: 'Contributor - /recall and /write access' },
  { value: 'manager', label: 'Manager - /recall, /write, /delete and /export access' }
];

const ttlOptions = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '365 days' },
];

const CreateKeyModal = ({ isOpen, onClose, onCreateKey, newlyCreatedKey }: CreateKeyModalProps) => {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(scopes.map(scope => scope.id));
  const [environment, setEnvironment] = useState('production');
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [role, setRole] = useState('manager');
  const [copied, setCopied] = useState(false);
  const [ttlDays, setTtlDays] = useState(30);
  const { toast } = useToast();

  // Get team subscription level
  const {
    data: team,
    isLoading: isTeamLoading,
  } = useQuery({
    queryKey: ['team'],
    queryFn: () => teamService.getTeam(),
  });

  const subscriptionLevel = team?.subscription || 1; // Default to 1 (Free) if not loaded
  const isFreeUser = subscriptionLevel === 1;

  // Filter TTL options based on subscription level
  const availableTtlOptions = isFreeUser 
    ? ttlOptions.filter(option => option.value <= 30)
    : ttlOptions;

  const resetState = () => {
    setStep('form');
    setKeyName('');
    setSelectedScopes(scopes.map(scope => scope.id));
    setEnvironment('production');
    setExpiryDate(undefined);
    setRole('manager');
    setCopied(false);
    setTtlDays(30);
  };

  const handleCreateKey = () => {
    if (!keyName) return;

    // Convert expiryDate to string format or null
    const expiresAt = expiryDate ? format(expiryDate, 'yyyy-MM-dd HH:mm:ss') : null;

    onCreateKey(
      keyName, 
      selectedScopes.length > 0 ? selectedScopes : scopes.map(s => s.id),
      environment,
      expiresAt,
      role,
      ttlDays
    );
    setStep('success');
  };

  const handleScopeToggle = (scopeId: string) => {
    setSelectedScopes(current =>
      current.includes(scopeId)
        ? current.filter(id => id !== scopeId)
        : [...current, scopeId]
    );
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>Create New API Key</DialogTitle>
              <DialogDescription>
                Generate a new API key for your integrations.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Key Name</Label>
                <Input
                  id="key-name"
                  placeholder="Production API Key"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  className="bg-recallio-medium-gray border-recallio-light-gray/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Environment</Label>
                <Select value={environment} onValueChange={setEnvironment}>
                  <SelectTrigger className="bg-recallio-medium-gray border-recallio-light-gray/50">
                    <SelectValue placeholder="Select environment" />
                  </SelectTrigger>
                  <SelectContent>
                    {environments.map((env) => (
                      <SelectItem key={env.value} value={env.value}>
                        {env.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="bg-recallio-medium-gray border-recallio-light-gray/50">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ttl-days">TTL (Time to Live)</Label>
                <Select value={ttlDays.toString()} onValueChange={(value) => setTtlDays(parseInt(value))}>
                  <SelectTrigger className="bg-recallio-medium-gray border-recallio-light-gray/50">
                    <SelectValue placeholder="Select TTL" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTtlOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isFreeUser && (
                  <p className="text-xs text-amber-600">
                    ⚠️ Free users are limited to 30 days. <a href="/billing" className="underline hover:text-amber-700">Upgrade your subscription</a> to access longer TTL periods up to 365 days.
                  </p>
                )}
              </div>

              

              <div className="space-y-2">
                <Label>Expiry Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal bg-recallio-medium-gray border-recallio-light-gray/50",
                        !expiryDate && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {expiryDate ? format(expiryDate, "PPP") : <span>No expiration</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={expiryDate}
                      onSelect={setExpiryDate}
                      initialFocus
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  The API key will automatically expire at 00:00 on the selected date.
                </p>
              </div>
            </div>

            <DialogFooter className="sm:justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                disabled={!keyName} 
                onClick={handleCreateKey}
                className="btn-primary"
              >
                Generate Key
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>API Key Generated</DialogTitle>
              <DialogDescription>
                Please copy your API key now. You won't be able to see it again!
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="api-key" className="sr-only">
                  API Key
                </Label>
                <div className="relative">
                  <Input
                    id="api-key"
                    readOnly
                    value={newlyCreatedKey || ''}
                    className="pr-12 font-geist-mono text-sm bg-recallio-medium-gray border-recallio-light-gray/50"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="absolute right-0 top-0 h-full aspect-square"
                    onClick={() => {
                      const keyToCopy = newlyCreatedKey || '';
                      navigator.clipboard.writeText(keyToCopy);
                      setCopied(true);
                      toast({
                        title: "API Key copied",
                        description: "The API key has been copied to your clipboard.",
                      });
                      
                      setTimeout(() => {
                        setCopied(false);
                      }, 2000);
                    }}
                  >
                    {copied ? <Check size={16} className="text-recallio-green" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>

              <div className="text-sm space-y-2 rounded-md bg-recallio-medium-gray p-4 border border-recallio-light-gray/50">
                <p><strong>Key Details:</strong></p>
                <p>Environment: <span className="font-medium">{environment}</span></p>
                <p>Role: <span className="font-medium">{role}</span></p>
                <p>TTL: <span className="font-medium">{ttlDays} days</span></p>
                {expiryDate && (
                  <p>Expires On: <span className="font-medium">{format(expiryDate, "PPP")}</span></p>
                )}
              </div>

              <p className="text-sm text-muted-foreground">
                Store this key securely. Treat it like a password.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateKeyModal;
